// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartKPIConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import constants from 'logic/data/Izenda.ReportPartFieldConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { values } from 'lodash/fp';

const FUNCTION = constants.FUNCTION;
const FORMAT_KEY = constants.FORMAT_KEY;
const CHART_TYPES = [
  {
    get label() {
      return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_LABEL_SIMPLE_GAUGE');
    },
    value: CHART_TYPE.SimpleGauge,
    className: 'gauge-simple'
  }
];

const TileTypes = {
  image: {
    id: 'kpi-image',
    menuLabel: localizer.getText('KPI_GRID_MENU_IMAGE')
  },
  metric: {
    id: 'kpi-metric',
    menuLabel: localizer.getText('KPI_GRID_MENU_METRIC')
  },
  text: {
    id: 'kpi-text',
    menuLabel: localizer.getText('KPI_GRID_MENU_TEXT')
  }
};

/* Container which defined fields to get data */
const ContainerName = {
  VALUES: 'values'
};

/* Default function for each fields based on its container */
const DefaultFunction = {};

DefaultFunction[CHART_TYPE.SimpleKPI] = {};

// SOLID GAUGE -  values container
DefaultFunction[CHART_TYPE.SimpleKPI][ContainerName.VALUES] = {
  Numeric: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Money: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Others: {
    FUNCTION: FUNCTION.COUNT,
    FORMAT: FORMAT_KEY.NONE
  }
};

/* Maximum number of fields for each container */
const MaximumField = {};

// Maximum number of fields for simple gauges
MaximumField[CHART_TYPE.SimpleKPI] = MaximumField[CHART_TYPE.SolidGauge];

/* Messages */
const Message = {
  get M01() {
    return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_MESSAGE_LOW_VALUE_MUST_LESS_THAN_HIGH_VALUE');
  },
  get M02() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOL_NUMBER');
  },
  get M04() {
    return localizer.getText('REPORT_PART_GAUGE_CONSTANTS_MESSAGE_THRESHOLDS_MUST_BE_WITHIN_SCALE_FORM_SCALE_TO');
  },
  get M05() {
    return localizer.getText('COMMON_MESSAGES_MUST_BE_FUNCTION');
  },
  get M06() {
    return localizer.getText('GAUGE_MESSAGE_SCALE_RANGE');
  },
  get M07() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOLD_MAX_VALUE');
  },
  get M08() {
    return localizer.getText('GAUGE_MESSAGE_THRESHOLD_MIN_VALUE');
  },
  get FIELDS_EXCEEDED_LIMITATION() {
    return localizer.getText('REPORTDESIGNER_MESSAGE_FIELDS_IN_REPORT_PART_EXCEEDED_LIMITATION');
  }
};

const KPI_REPORT_PART_PROPERTIES = {
  numRows: 15,
  numCols: 15,
  cellSize: 25
};

// define CUSTOM_URL
const CUSTOM_URL = {
  NAME: 'customURL',
  get CONTENT_DESCRIPTION() {
    return localizer.getText('PROPERTIES_BUILDER_CONSTANTS_CUSTOM_URL_CONTENT_DESCRIPTION');
  },
  CONSTRAINTS: {
    URL: {
      presence: {
        presence: true,
        get message() {
          return localizer.getText('PROPERTIES_BUILDER_CONSTANTS_CUSTOM_URL_CONSTRAINTS_MESSAGE_URL_IS_REQUIRED');
        }
      }
    }
  },
  DEFAULT_VALUE: 'LINK_NEW_WINDOW',
  OPTIONS: {
    LINK_NEW_WINDOW: 'LINK_NEW_WINDOW',
    LINK_NEW_TAB: 'LINK_NEW_TAB',
    LINK_CURRENT_WINDOW: 'LINK_CURRENT_WINDOW'
  }
};

export default {
  ContainerName,
  MaximumField,
  Message,
  DefaultFunction,
  DefaultChartType: CHART_TYPE.SimpleKPI,
  draggableClass: 'kpi-draggable',
  CHART_TYPES,
  TileTypes,
  ITEM_PER_ROWS: 3,
  KPI_REPORT_PART_PROPERTIES,
  CUSTOM_URL,
  getOptions(key) {
    const data = this[key] || {};

    return values(data);
  },
  DEFAULT_SETTING_VALUE: {
    refreshIntervalDefault: {
      enable: false,
      isAll: true,
      latestRecord: 0,
      updateInterval: 0
    }
  },
  textProperties: {
    alignment: 'left',
    font: {
      fontFamily: 'Proxima Nova',
      fontSize: 14
    },
    fontStyle: {
      fontBold: false,
      fontItalic: false,
      fontUnderline: false
    },
    color: {
      textColor: '#000000',
      backgroundColor: '#FFFFFF',
      alpha: 1,
      isAlpha: true
    },
    border: {
      top: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      right: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      bottom: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      left: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      }
    }
    
  },
  metricProperties: {
    border: {
      top: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      right: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      bottom: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      },
      left: {
        color: '#FFFFFF',
        dashStyle: 'none',
        thinkness: 1
      }
    },
    font: {
      fontFamily: 'Proxima Nova',
      fontSize: 14
    },
    fontStyle: {
      fontBold: false,
      fontItalic: false,
      fontUnderline: false
    },
    color: {
      textColor: '#000000',
      backgroundColor: '#FFFFFF',
      alpha: 1,
      isAlpha: true
    }
  },
  imageProperties: {
    image: ''
  }
};


// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartKPIConstants.js